import { useCallback, useEffect, useRef } from 'react'
import { Box, Flex, useDisclosure } from '@chakra-ui/react'
import rallyLogo from 'assets/images/rally-player-development-logo.png'
import rallyShield from 'assets/images/rally-player-development-shield.png'
import classNames from 'classnames'
import colors from 'theme/colors.theme'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { Link } from 'react-router-dom'
import SimpleBar from 'simplebar-react'
import 'simplebar-react/dist/simplebar.min.css'
import { AuthRoleIdEnum } from 'constants/user'
import { IGroupMemberPermissionId } from 'interfaces/group'
import { PLAY_DASHBOARD_BODY_BG, PLAY_TOPBAR_HEIGHT } from 'pages/PlayDashboardPage/mock-data'
import SideBarContent from './components/SideBarContent'
import { helpItem, manageOrgItem } from './components/SideBarContent/constants'
import { isSidebarItemActive, useActiveSidebarPath } from './components/SideBarContent/sidebar-active-path'
import MenuItemLink from './components/SideBarContent/components/MenuItemLink'
import CurrentVersionModal from './components/CurrentVersionModal'
import SvgIcon from 'components/SvgIcon'
import useFeatureFlagValue from 'hooks/useFeatureFlagValue'
import { EFeatureFlags } from 'constants/featureFlags'

interface ILeftSidebarProps {
  menuClickHandler?: {}
  hideLogo?: boolean
  isCondensed: boolean
  showSidebarMobile: boolean
  setShowSidebarMobile: (showSidebarMobile: boolean) => void
  activeProfileType?: import('constants/play-profile').PlayProfileRole
}

// Mirrors `$leftbar-width` in styles/themes/default/_custom-variables.scss and
// SIDEBAR_WIDTH_EXPANDED in PlayTopbar. Update all three together.
const SIDEBAR_WIDTH_EXPANDED = '220px'
const SIDEBAR_WIDTH_CONDENSED = '70px'

// "Manage your Org" call-to-action — Button Gradient/1 + Surface Stroke from the
// Org Admin sidebar design spec.
const MANAGE_ORG_BG_GRADIENT = 'linear(180deg, #004988 0%, #00417B 37.92%, #003366 100%)'
// Hover fill for the selected state — the same three stops lifted 15%. Kept as a
// gradient rather than `filter: brightness()` so the lift lands on the fill alone;
// a filter would brighten the label and icon along with it.
const MANAGE_ORG_BG_GRADIENT_HOVER = 'linear(180deg, #00549C 0%, #004B8D 37.92%, #003B75 100%)'
// Resting fill — Surface Gradient from the same spec. The CTA reads as a raised
// surface even when it is not the current screen; only the blue Button Gradient
// above marks selection.
const MANAGE_ORG_BG_GRADIENT_RESTING = 'linear(180deg, #172437 0%, #0E1928 100%)'
// Hover for the resting state, both stops lifted 15% like the selected pair. A
// `bg` tint cannot be used here: Chakra maps `bg` to the `background` shorthand,
// which resets `background-image` and would drop the gradient on hover.
const MANAGE_ORG_BG_GRADIENT_RESTING_HOVER = 'linear(180deg, #1A293F 0%, #101D2E 100%)'
const MANAGE_ORG_BORDER = 'rgba(194, 199, 214, 0.12)'
const MANAGE_ORG_LABEL_COLOR = '#F2F7FA'
// Same hairline as the sidebar's own right border.
const SIDEBAR_HAIRLINE = 'rgba(194,199,214,0.08)'

const LeftSidebar = (props: ILeftSidebarProps) => {
  const isCondensed = props.isCondensed || false
  const { isOpen: isVersionModalOpen, onClose: onVersionModalClose } = useDisclosure()
  const { userStore, authStore } = useStores()
  const showSidebarMobile = props.showSidebarMobile || false
  const hideLogo = props.hideLogo || false
  const { currentUserGroupMembers } = userStore
  // "Basic" now means PLAYER-only across all teams. A user with COACH-or-higher
  // in ANY team gets the admin-style chrome regardless of which org context they
  // last touched. Replaces the legacy single-membership lookup.
  const isBasicUser = !authStore.hasRoleAtLeast(AuthRoleIdEnum.COACH)
  const userCanManage =
    currentUserGroupMembers?.some(groupMember => groupMember?.permissionId === IGroupMemberPermissionId.EDITOR) ||
    !isBasicUser

  const { value: isReleaseNotesEnabled } = useFeatureFlagValue(EFeatureFlags.RELEASE_NOTES, true)

  // "Manage your Org" and "Help" belong to the Org Admin (Programing Admin)
  // chrome only — the basic-user and play-profile sidebars keep their own shape.
  const isOrgAdminContext = !isBasicUser && !props.activeProfileType
  // Managing the org itself is Owner/Administrator territory; a Coach gets the
  // same sidebar minus this button (and minus Player Groups / Reports, which are
  // filtered out by their own `allowedRoles`).
  const canManageOrg = isOrgAdminContext && authStore.hasAdminMembership
  // The CTA follows the same one-row-selected rule as the nav below it, so it only
  // wears its fill while the Organization page is the current screen.
  const isManageOrgActive = isSidebarItemActive(manageOrgItem, useActiveSidebarPath())

  const menuNodeRef = useRef<HTMLDivElement>(null)
  const handleClickOutSide = useCallback(
    event => {
      if (!menuNodeRef?.current?.contains(event?.target) && showSidebarMobile) {
        props.setShowSidebarMobile(false)
      }
    },
    [showSidebarMobile, menuNodeRef]
  )
  useEffect(() => {
    document.addEventListener('click', handleClickOutSide, true)
    return () => {
      document.removeEventListener('click', handleClickOutSide, true)
    }
  }, [handleClickOutSide])

  return (
    <Flex
      ref={menuNodeRef}
      // Intentionally NOT using the legacy `.left-side-menu` className — the global
      // styles/layouts/_left-menu.scss rules on that selector force `padding-top: 70px`
      // and `background: #2a3e60`, both of which clash with the redesigned topbar-flush
      // chrome. We provide a scoped `showSidebarMobile` hook below for the mobile drawer.
      className={classNames(props.showSidebarMobile ? 'showSidebarMobile' : '')}
      direction="column"
      justifyContent="space-between"
      position="fixed"
      top={0}
      bottom={0}
      width={isCondensed ? SIDEBAR_WIDTH_CONDENSED : SIDEBAR_WIDTH_EXPANDED}
      height="100%"
      zIndex={1002}
      paddingTop={0}
      bg={PLAY_DASHBOARD_BODY_BG}
      borderRight="1px solid rgba(194,199,214,0.08)"
      boxShadow="none"
      transition="all 0.2s ease-out"
      sx={{
        // Must end where the layout SCSS stops reserving this column
        // (`media-breakpoint-down(sm)`), or on that one width the bar slides away
        // and leaves its 220px gutter behind — what an iPad mini showed.
        '@media (max-width: 767.98px)': {
          left: '-100%',
          opacity: 0,
          transition: 'all 0.3s ease-in'
        },
        '&.showSidebarMobile': { left: 0, opacity: 1 }
      }}
    >
      <Flex direction="column" flex="1" minHeight={0} overflow="hidden">
        {!hideLogo && (
          <Link
            to="/"
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: PLAY_TOPBAR_HEIGHT,
              flexShrink: 0,
              padding: isCondensed ? '0' : '0 16px',
              backgroundColor: PLAY_DASHBOARD_BODY_BG
            }}
          >
            <img
              src={isCondensed ? rallyShield : rallyLogo}
              style={{
                height: isCondensed ? '28px' : '32px',
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
                display: 'block'
              }}
              alt="Rally Player Development"
            />
          </Link>
        )}

        {canManageOrg && (
          // Links to the Organization page. Visibility is already gated by
          // `canManageOrg`, so no role check is repeated here. It always wears the
          // surface gradient and hairline; selection swaps that fill for the blue
          // button gradient, matching the one-row-selected rule of the nav below.
          <Box
            as={Link}
            to={manageOrgItem.path}
            title={isCondensed ? manageOrgItem.name : undefined}
            display="flex"
            alignItems="center"
            justifyContent={isCondensed ? 'center' : 'flex-start'}
            gap="8px"
            flexShrink={0}
            height="32px"
            marginX="12px"
            marginTop="8px"
            marginBottom="8px"
            paddingX={isCondensed ? 0 : '12px'}
            border="1px solid"
            borderColor={MANAGE_ORG_BORDER}
            borderRadius="8px"
            bgGradient={isManageOrgActive ? MANAGE_ORG_BG_GRADIENT : MANAGE_ORG_BG_GRADIENT_RESTING}
            color={isManageOrgActive ? MANAGE_ORG_LABEL_COLOR : colors.gray[200]}
            _hover={{
              textDecoration: 'none',
              // The label keeps its resting colour on hover. Restated here because the
              // row renders as an <a> and the globally imported bootstrap stylesheet
              // carries `a:hover { color }`, whose type+pseudo-class specificity
              // outranks Chakra's generated class and would otherwise repaint the text.
              color: isManageOrgActive ? MANAGE_ORG_LABEL_COLOR : colors.gray[200],
              bgGradient: isManageOrgActive ? MANAGE_ORG_BG_GRADIENT_HOVER : MANAGE_ORG_BG_GRADIENT_RESTING_HOVER
            }}
          >
            {/* No `color` prop: this icon is stroke-based with `currentColor`, and
                SvgIcon's color override rewrites every `fill` (including the root
                `fill="none"`), which would turn the outlined squares solid. */}
            {manageOrgItem.iconName && (
              <Box flexShrink={0} display="flex">
                <SvgIcon size={20} iconName={manageOrgItem.iconName} />
              </Box>
            )}
            {!isCondensed && (
              <Box
                as="span"
                flexGrow={1}
                display="flex"
                alignItems="center"
                fontFamily="Poppins"
                fontSize="14px"
                fontWeight={500}
                lineHeight="20px"
                whiteSpace="nowrap"
              >
                {manageOrgItem.name}
              </Box>
            )}
          </Box>
        )}

        <Box flex="1" minHeight={0} overflow="hidden">
          {/* Both expanded and condensed modes wrap SideBarContent in SimpleBar
              so the nav scrolls when the viewport can't fit every item. */}
          <SimpleBar style={{ height: '100%' }} timeout={500} scrollbarMaxSize={320}>
            <SideBarContent
              isCondensed={isCondensed}
              isBasicUser={isBasicUser}
              haveEditorRole={userCanManage}
              activeProfileType={props.activeProfileType}
            />
          </SimpleBar>
        </Box>
      </Flex>

      {isOrgAdminContext && (
        <Box flexShrink={0} paddingY="8px" borderTop="1px solid" borderTopColor={SIDEBAR_HAIRLINE}>
          <MenuItemLink item={helpItem} isCondensed={isCondensed} />
        </Box>
      )}

      {isReleaseNotesEnabled && (
        <CurrentVersionModal isOpen={isVersionModalOpen} onClose={onVersionModalClose} />
      )}
    </Flex>
  )
}

export default observer(LeftSidebar)
